import { useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'

import {
  subscribeToWorkspaceRepeatingEvents,
  subscribeToMyWorkspaces,
  subscribeToWorkspaceCalendarEvents,
  subscribeToWorkspaceInvitations,
  subscribeToUserProfile,
} from '../db'
import {
  selectIsAuthenticated,
  setUserProfile,
  selectUserId,
} from '../../store/slices/userSlice'
import {
  addCalendarEvents,
  addRepeatingEvents,
  selectCurrentWorkspace,
  setWorkspaces,
  selectCalendarDateRange,
  setInvitations,
  selectWorkspaces,
} from '../../store/slices/workspaceSlice'
import {
  transformWorkspace,
  transformWorkspaceInvitation,
  transformUser,
} from '../db/db.transforms'

const useDataListener = () => {
  const dispatch = useDispatch()

  const userId = useSelector(selectUserId)
  const isAuthenticated = useSelector(selectIsAuthenticated)

  const currentWorkspace = useSelector(selectCurrentWorkspace)
  const workspaces = useSelector(selectWorkspaces)
  const dateRange = useSelector(selectCalendarDateRange)

  useEffect(() => {
    if (!!userId && !!isAuthenticated) {
      const unsubscribe = subscribeToUserProfile({
        userId,
        onUpdate: (val) => {
          if (val) {
            dispatch(setUserProfile(transformUser(val)))
          }
        },
      })
      return () => unsubscribe()
    }
  }, [dispatch, userId, isAuthenticated])

  useEffect(() => {
    if (!!userId && !!isAuthenticated) {
      const unsubscribe = subscribeToMyWorkspaces({
        userId,
        onUpdate: (val) => {
          dispatch(setWorkspaces(val.map((w) => transformWorkspace(w))))
        },
      })
      return () => unsubscribe()
    }
  }, [dispatch, userId, isAuthenticated])

  useEffect(() => {
    if (isAuthenticated && workspaces.length > 0) {
      const listeners = workspaces.map((w) => {
        return subscribeToWorkspaceRepeatingEvents({
          workspaceId: w.id,
          onUpdate: (val) => {
            dispatch(
              addRepeatingEvents({
                workspaceId: w.id,
                events: val.map((v) => ({ ...v, workspaceId: w.id })),
              }),
            )
          },
        })
      })
      return () => listeners.forEach((l) => l())
    }
  }, [dispatch, isAuthenticated, workspaces])

  useEffect(() => {
    if (isAuthenticated && workspaces.length > 0) {
      const listeners = workspaces.map((w) => {
        return subscribeToWorkspaceCalendarEvents({
          workspaceId: w.id,
          startDate: dateRange.startDate,
          endDate: dateRange.endDate,
          onUpdate: (val) => {
            dispatch(
              addCalendarEvents({
                workspaceId: w.id,
                events: val.map((e) => ({
                  ...e,
                  workspaceId: w.id,
                  workspaceType: w.spaceType,
                })),
              }),
            )
          },
        })
      })
      return () => listeners.forEach((l) => l())
    }
  }, [
    dispatch,
    isAuthenticated,
    workspaces,
    dateRange.startDate,
    dateRange.endDate,
  ])

  useEffect(() => {
    if (!!isAuthenticated && !!currentWorkspace) {
      const unsubscribe = subscribeToWorkspaceInvitations({
        workspaceId: currentWorkspace,
        onUpdate: (val) => {
          dispatch(
            setInvitations({
              workspaceId: currentWorkspace,
              invitations: val.map((i) => transformWorkspaceInvitation(i)),
            }),
          )
        },
      })
      return () => unsubscribe()
    }
  }, [dispatch, isAuthenticated, currentWorkspace])
}

export const DataListener = () => {
  useDataListener()
  return null
}
